import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import {
  configurationSummary,
  overviewSupporterPaidIndicator,
} from '../../data/platform-result-presentation';
import type {
  PlatformCategoryDefinition,
  PlatformComparisonResult,
  PlatformId,
  PlatformOptionChange,
  PlatformOverviewViewModel,
  PlatformPricingConfiguration,
} from '../../models/fundraising-comparison.models';
import { OrganizationReceivesChartComponent } from '../organization-receives-chart/organization-receives-chart.component';
import { PlatformOverviewCardComponent } from '../platform-overview-card/platform-overview-card.component';
import { PlatformQuickViewComponent } from '../platform-quick-view/platform-quick-view.component';

@Component({
  standalone: true,
  selector: 'app-fundraising-explorer-results',
  imports: [
    CommonModule,
    OrganizationReceivesChartComponent,
    PlatformOverviewCardComponent,
    PlatformQuickViewComponent,
  ],
  templateUrl: './fundraising-explorer-results.component.html',
  host: { class: 'd-block' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FundraisingExplorerResultsComponent {
  @Input({ required: true }) results: PlatformComparisonResult[] = [];
  @Input({ required: true }) platforms: PlatformPricingConfiguration[] = [];
  @Input({ required: true }) categories: PlatformCategoryDefinition[] = [];
  @Input() detailPageUrls: Partial<Record<PlatformId, string>> = {};
  @Output() optionChange = new EventEmitter<PlatformOptionChange>();
  @Output() quickViewOpened = new EventEmitter<PlatformId>();
  @Output() quickViewClosed = new EventEmitter<PlatformId>();
  @Output() quickViewFullPageClicked = new EventEmitter<PlatformId>();
  @Output() detailLinkClicked = new EventEmitter<PlatformId>();

  activePlatformId?: PlatformId;

  get rows(): PlatformOverviewViewModel[] {
    const platformsById = new Map(
      this.platforms.map((platform) => [platform.id, platform]),
    );

    return this.results.flatMap((result) => {
      const platform = platformsById.get(result.platformId);
      if (!platform) return [];
      const organizationReceivesCents = Math.max(
        0,
        result.organizationRetainedCents,
      );
      const retainedOriginalDonationCents = Math.max(
        0,
        Math.min(
          result.intendedGiftTotalCents,
          result.intendedGiftTotalCents - result.organizationCostCents,
        ),
      );
      const retentionPercentage =
        result.intendedGiftTotalCents === 0
          ? 0
          : Math.min(
              100,
              (retainedOriginalDonationCents / result.intendedGiftTotalCents) *
                100,
            );
      return [
        {
          result,
          categoryId: platform.categoryId,
          configurationSummary: configurationSummary(result),
          supporterPaidIndicator: overviewSupporterPaidIndicator(result),
          organizationReceivesCents,
          organizationReceivesOverageCents: Math.max(
            0,
            organizationReceivesCents - result.intendedGiftTotalCents,
          ),
          retainedOriginalDonationCents,
          retentionPercentage,
          costsExceedDonationsCents: Math.max(
            0,
            -result.organizationRetainedCents,
          ),
          detailPageUrl: this.detailPageUrls[result.platformId],
        },
      ];
    });
  }

  get activeRow(): PlatformOverviewViewModel | undefined {
    return this.rows.find(
      (row) => row.result.platformId === this.activePlatformId,
    );
  }

  get sortedRows(): PlatformOverviewViewModel[] {
    return [...this.rows].sort((left, right) =>
      left.result.platformName.localeCompare(
        right.result.platformName,
        'en-US',
        { sensitivity: 'base' },
      ),
    );
  }

  openQuickView(platformId: PlatformId): void {
    this.activePlatformId = platformId;
    this.quickViewOpened.emit(platformId);
  }

  rowsForCategory(
    category: PlatformCategoryDefinition,
  ): PlatformOverviewViewModel[] {
    return this.rows.filter((row) => row.categoryId === category.id);
  }

  trackCategory(_: number, category: PlatformCategoryDefinition): string {
    return category.id;
  }

  trackRow(_: number, row: PlatformOverviewViewModel): string {
    return row.result.platformId;
  }
}
